/* Owns the browser entry point: the query client with the `staleTime` policy of
   PANEL.md §11, the provider order of §1, the theme stamp and the optional MSW
   layer. Nothing else in the app creates a `QueryClient` or a router. */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router/dom'

import './index.css'
import { ToastViewport } from './components/ui/Toast.js'
import { LangProvider } from './i18n/index.js'
import { ApiHttpError } from './lib/api.js'
import { GC_TIME, STALE } from './lib/queryKeys.js'
import { initTheme } from './lib/theme.js'
import { router } from './router.js'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE.default,
      gcTime: GC_TIME,
      // Refetching on focus would re-read every list every time an operator
      // alt-tabs back; the config page opts back in because a stale
      // `baseVersion` is the one failure mode that matters (PANEL.md §11).
      refetchOnWindowFocus: false,
      retry: (failureCount: number, error: unknown) => {
        // A 4xx is the server's final answer — retrying a validation error or a
        // 403 only burns the caller's rate-limit budget. A 429 is already
        // retried once inside `lib/api.ts`.
        if (error instanceof ApiHttpError && error.status >= 400 && error.status < 500) return false
        return failureCount < 1
      },
    },
  },
})

initTheme()

async function startMocks(): Promise<void> {
  // A dynamic import, so MSW and the fixtures land in their own chunk that is
  // only ever fetched when the flag is set — a production visitor never
  // downloads a byte of the mock layer.
  if (import.meta.env['VITE_USE_MOCK'] !== '1') return
  const { startMockWorker } = await import('./mocks/browser.js')
  await startMockWorker()
}

const container = document.getElementById('root')
if (!container) throw new Error('#root is missing from index.html')

/**
 * Mocks must never decide whether the application renders.
 *
 * `startMocks` registers a service worker, and that can fail for reasons that
 * have nothing to do with this code: an insecure context, a browser with
 * workers disabled, a locked-down embedded view. Without this catch the
 * rejection skipped `render` entirely and the panel was a blank page with one
 * console error — the same symptom as a real crash, and a much harder one to
 * diagnose. A failed mock layer is worth a warning, never a white screen.
 */
void startMocks()
  .catch((error: unknown) => {
    console.warn('[SentinelAC] mock layer failed to start; continuing against the real API', error)
  })
  .then(() => {
    createRoot(container).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <LangProvider>
            {/* The session lives in `lib/auth-store.ts`, a module singleton read
                through `useSyncExternalStore` (PANEL.md §11), so it needs no
                provider node — `RequireAuth` bootstraps it inside the router. */}
            <RouterProvider router={router} />
            <ToastViewport />
          </LangProvider>
        </QueryClientProvider>
      </StrictMode>,
    )
  })
